import { create } from 'zustand';
import type {
  CarrierDto,
  OrderDto,
  OrderEditPreviewDto,
  OrderNotificationType,
  OrderStatus,
  OrderStatusCountsDto,
  RefundOrderRequest,
  UpdateOrderAddressRequest,
  UpdateOrderDetailsRequest,
  UpdateOrderItemsRequest,
  UpdateOrderStatusRequest,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { saveFile } from '@/lib/download';
import { useWorkspaceStore } from './workspace.store';
import { riyalsToHalalas } from '@/lib/price-input';

/**
 * Orders — the first screen built, because it is the one the store cannot
 * operate without (07-Admin/Dashboard.md build order).
 *
 * The list is cursor-paginated and searched server-side; it never holds the
 * whole table in memory and never filters client-side.
 */

export interface OrderFilters {
  search: string;
  /** Several at once; empty means every status. */
  statuses: OrderStatus[];
  paymentMethod: '' | 'COD' | 'CARD' | 'MANUAL';
  /** `YYYY-MM-DD`. */
  from: string;
  to: string;
  /** Riyals as typed; converted to halalas on the way out. */
  minTotal: string;
  maxTotal: string;
  /** Newest first by default; oldest first is the queue to work through. */
  sort: 'newest' | 'oldest';
  /** '' anyone, `me`, `none`, or a member of staff's id (ADR-074). */
  assignee: string;
}

export const EMPTY_ORDER_FILTERS: OrderFilters = {
  search: '',
  statuses: [],
  paymentMethod: '',
  from: '',
  to: '',
  minTotal: '',
  maxTotal: '',
  sort: 'newest',
  assignee: '',
};

interface OrdersState {
  items: OrderDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: OrderFilters;
  /** How many match the filter in total, not just on this page. */
  total: number | null;

  /** How many orders sit in each status — the tabs' counts. Null until read. */
  counts: OrderStatusCountsDto | null;
  exporting: boolean;

  /** The order open in the detail view. */
  current: OrderDto | null;
  currentLoading: boolean;
  currentError: string | null;
  updating: boolean;
  /** What the ship dialog offers; loaded once, when first needed. */
  carriers: CarrierDto[] | null;

  load: (filters?: Partial<OrderFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  loadCounts: () => Promise<void>;
  /** The current filter as a spreadsheet, built on the server. */
  exportCsv: () => Promise<void>;
  open: (id: string) => Promise<void>;
  updateStatus: (id: string, change: UpdateOrderStatusRequest) => Promise<boolean>;
  /**
   * The same change, one order at a time, for a selection on the list. Each
   * order keeps its own rules — the API may refuse one and accept the rest —
   * so the answer says which went through and why the others did not.
   */
  updateMany: (
    ids: string[],
    change: UpdateOrderStatusRequest,
  ) => Promise<{ done: number; failed: { id: string; number: string; message: string }[] }>;
  addNote: (id: string, note: string) => Promise<boolean>;
  /** Gives the open order to someone, or to nobody (ADR-074). */
  assign: (id: string, assigneeId: string | null) => Promise<boolean>;
  /** "Deliver now": waiting lines handed over, the downloads mailed again (ADR-077). */
  deliverDigital: (id: string) => Promise<boolean>;
  /** The same for a selection, one at a time — the refused named. */
  assignMany: (ids: string[], assigneeId: string | null) => Promise<{ done: number; failed: { id: string; number: string; message: string }[] }>;
  resend: (id: string, type: OrderNotificationType) => Promise<boolean>;
  /** Street, contact and directions before it leaves; the city stays (ADR-059). */
  updateAddress: (id: string, address: UpdateOrderAddressRequest) => Promise<boolean>;
  /** Several orders read whole — the packing slips printed for a selection. */
  fetchMany: (ids: string[]) => Promise<OrderDto[]>;
  /** Money back without a return (ADR-061). */
  refund: (id: string, request: RefundOrderRequest) => Promise<boolean>;
  loadCarriers: () => Promise<void>;
  /** Contact, the customer's note, billing (ADR-062). */
  updateDetails: (id: string, change: UpdateOrderDetailsRequest) => Promise<boolean>;
  /** What an items change would cost; null when it was refused (the reason is in currentError). */
  previewItems: (id: string, change: UpdateOrderItemsRequest) => Promise<OrderEditPreviewDto | null>;
  updateItems: (id: string, change: UpdateOrderItemsRequest) => Promise<boolean>;
  /** A dialog opening starts clean, not with the last one's refusal. */
  clearError: () => void;
}

const PAGE_SIZE = 20;

export const useOrdersStore = create<OrdersState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  error: null,
  filters: EMPTY_ORDER_FILTERS,
  total: null,

  counts: null,
  exporting: false,

  current: null,
  currentLoading: false,
  currentError: null,
  updating: false,
  carriers: null,

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.orders.list(toQuery(next, PAGE_SIZE)));

      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
      void get().loadCounts();
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load orders.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.orders.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );

      set({
        items: [...items, ...page.items],
        nextCursor: page.meta.nextCursor,
        loadingMore: false,
      });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more orders.') });
    }
  },

  async loadCounts() {
    try {
      const counts = await withSession(() => adminApi.orders.counts());
      set({ counts });
    } catch {
      // The tabs read fine without their numbers; the list is what matters.
      set({ counts: null });
    }
  },

  async exportCsv() {
    const { filters, exporting } = get();
    if (exporting) return;
    set({ exporting: true, error: null });
    try {
      const query = toQuery(filters, 0);
      delete (query as { limit?: number }).limit;
      await saveFile(() => withSession(() => adminApi.orders.export(query)), 'orders.csv');
    } catch (error) {
      set({ error: errorMessage(error, 'Could not export orders.') });
    } finally {
      set({ exporting: false });
    }
  },

  async open(id) {
    // Show the row we already have while the full order loads, so the header
    // does not flash empty on the way in.
    const known = get().items.find((order) => order.id === id) ?? null;
    set({ current: known, currentLoading: true, currentError: null });

    try {
      const order = await withSession(() => adminApi.orders.get(id));
      set({ current: order, currentLoading: false });
    } catch (error) {
      set({ currentLoading: false, currentError: errorMessage(error, 'Could not load this order.') });
    }
  },

  async updateStatus(id, change) {
    const done = await act(set, id, () => adminApi.orders.updateStatus(id, change), 'Could not update this order.');
    // The menu's count of orders to fulfil has just changed.
    if (done) void useWorkspaceStore.getState().refreshAttention();
    return done;
  },

  async updateMany(ids, change) {
    set({ updating: true });
    const failed: { id: string; number: string; message: string }[] = [];
    let done = 0;

    // One at a time: each is its own transition with its own stock and mail,
    // and a burst of parallel writes is how a small server falls over.
    for (const id of ids) {
      try {
        const updated = await withSession(() => adminApi.orders.updateStatus(id, change));
        done += 1;
        set((state) => ({ items: state.items.map((order) => (order.id === id ? updated : order)) }));
      } catch (error) {
        const number = get().items.find((order) => order.id === id)?.number ?? id;
        failed.push({ id, number, message: errorMessage(error, 'Refused.') });
      }
    }

    set({ updating: false });
    void get().loadCounts();
    void useWorkspaceStore.getState().refreshAttention();
    return { done, failed };
  },

  async addNote(id, note) {
    return act(set, id, () => adminApi.orders.addNote(id, { note }), 'Could not save the note.');
  },

  async assign(id, assigneeId) {
    return act(set, id, () => adminApi.orders.assign(id, assigneeId), 'Could not assign the order.');
  },

  async deliverDigital(id) {
    return act(set, id, () => adminApi.orders.deliverDigital(id), 'Could not deliver the files and codes.');
  },

  async assignMany(ids, assigneeId) {
    set({ updating: true });
    const failed: { id: string; number: string; message: string }[] = [];
    let done = 0;
    for (const id of ids) {
      try {
        const updated = await withSession(() => adminApi.orders.assign(id, assigneeId));
        done += 1;
        set((state) => ({ items: state.items.map((order) => (order.id === id ? updated : order)) }));
      } catch (error) {
        const number = get().items.find((order) => order.id === id)?.number ?? id;
        failed.push({ id, number, message: errorMessage(error, 'Refused.') });
      }
    }
    set({ updating: false });
    return { done, failed };
  },

  async resend(id, type) {
    return act(set, id, () => adminApi.orders.resendNotification(id, { type }), 'Could not send it again.');
  },

  async updateAddress(id, address) {
    return act(set, id, () => adminApi.orders.updateAddress(id, address), 'Could not save the address.');
  },

  async fetchMany(ids) {
    const orders: OrderDto[] = [];
    for (const id of ids) orders.push(await withSession(() => adminApi.orders.get(id)));
    return orders;
  },

  async refund(id, request) {
    return act(set, id, () => adminApi.orders.refund(id, request), 'Could not refund this order.');
  },

  async updateDetails(id, change) {
    return act(set, id, () => adminApi.orders.updateDetails(id, change), 'Could not change this order.');
  },

  async previewItems(id, change) {
    try {
      const preview = await withSession(() => adminApi.orders.previewItems(id, change));
      set({ currentError: null });
      return preview;
    } catch (error) {
      set({ currentError: errorMessage(error, 'Could not price this change.') });
      return null;
    }
  },

  async updateItems(id, change) {
    return act(set, id, () => adminApi.orders.updateItems(id, change), 'Could not change the items.');
  },

  async loadCarriers() {
    if (get().carriers) return;
    try {
      const page = await withSession(() => adminApi.orders.carriers());
      set({ carriers: page.items });
    } catch {
      // No list is not a blocker: the dialog falls back to typing the carrier.
      set({ carriers: [] });
    }
  },

  clearError() {
    set({ currentError: null });
  },
}));

/**
 * One shape for every change made from the order screen: run it, show the
 * order the API returns, and keep the list row in step without a refetch. The
 * API owns the rules; a refusal comes back worded and is shown verbatim.
 */
async function act(
  set: (partial: Partial<OrdersState> | ((state: OrdersState) => Partial<OrdersState>)) => void,
  id: string,
  call: () => Promise<OrderDto>,
  fallback: string,
): Promise<boolean> {
  set({ updating: true, currentError: null });
  try {
    const updated = await withSession(call);
    set((state) => ({
      current: updated,
      updating: false,
      items: state.items.map((order) => (order.id === id ? updated : order)),
    }));
    return true;
  } catch (error) {
    set({ updating: false, currentError: errorMessage(error, fallback) });
    return false;
  }
}

/**
 * Filters as the API wants them.
 *
 * Money is typed in riyals and converted on the digit string, like every other
 * amount an operator enters (ADR-004); an empty field is absent rather than
 * zero, because "no minimum" and "at least nothing" are different questions.
 */
function toQuery(filters: OrderFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    paymentMethod: filters.paymentMethod || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    minTotal: riyalsToHalalas(filters.minTotal) ?? undefined,
    maxTotal: riyalsToHalalas(filters.maxTotal) ?? undefined,
    ...(filters.sort === 'oldest' ? { sort: 'oldest' as const } : {}),
    assignee: filters.assignee || undefined,
  };
}
